import { LitElement, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsThree";
import "@phosphor-icons/webcomponents/PhPencilLine";
import "@phosphor-icons/webcomponents/PhTrashSimple";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "ui-components/item-card";
import { NotificationService } from "ui-components/notification/notification-service";

import { styles } from "./link-card.css";

import { deleteUnifiedLink } from "../../services/unified-link-mutation";
import { t, translate } from "../../translations/i18n";
import "../create-link-modal/new-create-link-modal";
import type { UnifiedLink } from "api-contracts/rpc/get-unified-links";

@customElement("link-card")
export class LinkCard extends LitElement {
  static override styles = styles;
  @property({ type: Object })
  link!: UnifiedLink;

  @property({ type: Boolean })
  flourish = false;

  @state()
  private showCreateModal = false;

  @state()
  private deleteConfirmationLinkId: string | undefined;

  private handleDropdownItemClick = (event: CustomEvent, linkId: string) => {
    const { action } = event.detail;

    if (action === "edit") {
      this.showCreateModal = true;

      return;
    }

    if (action === "delete") {
      // Set the link ID for deletion confirmation
      this.deleteConfirmationLinkId = linkId;
      return;
    }
  };

  private handleModalClose = () => {
    this.showCreateModal = false;
  };

  private handleDeleteConfirm = async () => {
    if (!this.deleteConfirmationLinkId) return;

    const linkTitle = this.link.title || "";

    try {
      await deleteUnifiedLink.mutate({ linkId: this.deleteConfirmationLinkId });

      this.dispatchEvent(new CustomEvent("link-deleted"));

      // Close modal
      this.deleteConfirmationLinkId = undefined;

      // Show success notification
      NotificationService.success(translate("deleteLink.deleteSuccess", { title: linkTitle }));
    } catch (error) {
      console.error("Error deleting link:", error);

      // Show error notification
      NotificationService.error(translate("deleteLink.deleteError"));
    }
  };

  get cardType() {
    if (Boolean(this.link.thumbnail || this.link.promotion_file?.path) && !this.link.has_discount) return "square";
    return "rectangle";
  }

  private get getStatusFromDiscountStatus(): "success" | "warning" | "error" | undefined {
    if (!this.link.has_discount) return undefined;
    switch (this.link.status) {
      case "live":
        return "success";
      case "upcoming":
        return "warning";
      case "expired":
        return "error";
      default:
        return undefined;
    }
  }

  override render() {
    const type = this.cardType;
    const subtitle = this.link.has_discount
      ? this.link.teaser || (this.link.code ? `Code: ${this.link.code}` : this.link.url)
      : this.link.url;

    return html`
      <item-card
        .type=${type}
        .item=${{
          id: this.link.id,
          title: this.link.title,
          subtitle,
          imageUrl: this.link.promotion_file?.path || this.link.thumbnail,
          exclusive: this.link.exclusive,
          waiting_list: this.link.waiting_list,
        }}
        .flourish=${this.flourish}
        .status=${this.getStatusFromDiscountStatus}
      >
        <dropdown-menu
          slot="actions"
          placement="bottom-end"
          @dropdown-item-click=${(event: CustomEvent) => this.handleDropdownItemClick(event, this.link.id)}
        >
          <ph-dots-three slot="trigger" size="24"></ph-dots-three>
          <dropdown-menu-item
            ?disabled=${this.link.exclusive || this.link.waiting_list}
            action="edit"
            label=${t("links.menu.edit")}
            ><ph-pencil-line slot="icon" size="16"></ph-pencil-line
          ></dropdown-menu-item>
          <dropdown-menu-item
            ?disabled=${this.link.exclusive || this.link.waiting_list}
            action="delete"
            label=${t("links.menu.delete")}
            ><ph-trash-simple slot="icon" size="16"></ph-trash-simple
          ></dropdown-menu-item>
        </dropdown-menu>

        <div slot="modals">
          ${this.showCreateModal && this.link.id
            ? html`<new-create-link-modal
                .linkId=${this.link.id}
                @modal-close=${this.handleModalClose}
              ></new-create-link-modal>`
            : ""}
          ${this.deleteConfirmationLinkId !== undefined
            ? html`<wow-confirmation header=${t("deleteLink.title")}>
                <div>
                  <p>
                    ${t("deleteLink.confirmMessage", {
                      title: this.link.title,
                    })}
                  </p>
                  <section class="delete-confirmation-modal-footer">
                    <wow-button
                      variant="neutral"
                      size="medium"
                      @click=${() => {
                        this.deleteConfirmationLinkId = undefined;
                      }}
                      >${t("deleteLink.cancel")}</wow-button
                    >
                    <wow-button variant="primary" size="medium" @click=${this.handleDeleteConfirm}
                      >${t("deleteLink.delete")}</wow-button
                    >
                  </section>
                </div>
              </wow-confirmation>`
            : nothing}
        </div>
      </item-card>
    `;
  }
}
